// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'wouter';
import { Button } from '../components/ui/button';
import ProgressBar from '../components/ProgressBar';
import { telechargerRapport, telechargerExcel, marquerResultatVu, annulerAnalyse, estEnCours } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { APP_VERSION } from '../data/changelog';

// Trois positions, trois mots (doctrine du 13/08/2026).
//
// L'ancienne table était indexée sur EXCELLENT / BON / ACCEPTABLE /
// INSUFFISANT — un enum qui n'existe plus : `BADGE[meta.conformity_level]`
// rendait `undefined` pour tous les niveaux réels sauf NON_CONFORME.
//
// ⚠️ Le bloc d'affichage correspondant est COMMENTÉ dans le JSX plus bas : ce
// badge n'apparaît aujourd'hui nulle part. La table est corrigée pour être
// juste le jour où il sera réaffiché — le réactiver est une décision produit.
//
// `cle` et non `label` : les libellés passent par i18n, et `t()` ne peut pas
// être appelé au niveau module.
const BADGE = {
  FAVORABLE:   { bg: '#f0fdf4', color: '#16a34a', border: '#86efac', cle: 'avis_favorable' },
  RESERVE:     { bg: '#fefce8', color: '#ca8a04', border: '#fde047', cle: 'avis_reserve' },
  DEFAVORABLE: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', cle: 'avis_defavorable' },
};

// Position du rapport, déduite des COMPTEURS D'AVIS (posés par flask_app.py)
// et non d'un niveau global : un seul avis défavorable suffit à ce que le
// rapport en porte un.
const positionDuRapport = (meta) => {
  if ((Number(meta?.nb_non_conformes) || 0) > 0) return 'DEFAVORABLE';
  if ((Number(meta?.nb_reserves) || 0) > 0)      return 'RESERVE';
  if ((Number(meta?.nb_conformes) || 0) > 0)     return 'FAVORABLE';
  return null;  // aucune règle évaluée
};

const AnalysisView = () => {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const { t } = useI18n();
  const { user } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    setMessages([]);

    fetch(`${API_BASE_URL}/api/analyses/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject('Analyse introuvable'))
      .then(data => { setAnalysis(data); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });

    // L'historique de NORMA vit sur le SERVEUR (chantier ⑮) : plus de copie
    // locale en repli — elle était propre au navigateur, et lisible du compte
    // suivant sur le même poste.
    fetch(`${API_BASE_URL}/api/analyses/${id}/norma-messages`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setMessages((data?.messages || []).filter(m => m && m.text)))
      .catch(() => {});

    // Ouvrir l'analyse, c'est en voir le résultat : le bandeau de suivi n'en
    // parle plus (sans effet si elle n'est pas terminée, ou pas la sienne).
    marquerResultatVu(id);
  }, [id]);  // ← fermeture correcte

  // Analyse EN COURS (01/10/2026) : elle continue sur le serveur, la page la
  // suit — relue toutes les 10 s jusqu'à sa fin. Terminée sous nos yeux, elle
  // est vue (bandeau de suivi), et l'historique de NORMA est relu.
  const enCours = estEnCours(analysis);
  useEffect(() => {
    if (!id || !enCours) return undefined;
    const minuterie = setInterval(() => {
      fetch(`${API_BASE_URL}/api/analyses/${id}`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (!data) return;
          setAnalysis(data);
          if (data.status === 'COMPLETED') {
            marquerResultatVu(id);
            fetch(`${API_BASE_URL}/api/analyses/${id}/norma-messages`)
              .then(r => (r.ok ? r.json() : null))
              .then(d => setMessages((d?.messages || []).filter(m => m && m.text)))
              .catch(() => {});
          }
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(minuterie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, enCours]);

  // Le serveur n'accepte l'annulation que de l'auteur, ou d'un admin Evalens.
  const peutAnnuler = enCours && (analysis?.user_id === user?.id || user?.role === 'admin');
  const handleCancel = async () => {
    if (!window.confirm(t('confirm_cancel_analysis'))) return;
    try {
      await annulerAnalyse(id);
      setAnalysis(a => ({ ...a, status: 'CANCELLED' }));
    } catch (err) {
      alert(`${t('err_prefix')}${err.message}`);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const renderText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i, arr) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <span key={i}>
          {parts.map((p, j) => j % 2 === 1 ? <strong key={j}>{p}</strong> : p)}
          {i < arr.length - 1 && <br />}
        </span>
      );
    });
  };

  const handleCopyResume = () => {
    if (!analysis) return;
    let meta = {};
    try { meta = analysis.metadata ? JSON.parse(analysis.metadata) : {}; } catch(_) {}
    const score = meta.conformity_score != null ? Math.round(meta.conformity_score) : null;
    let text = `Analyse Normascan — ${analysis.filename}\n`;
    text += `${t('copy_date')} : ${new Date(analysis.upload_date).toLocaleDateString('fr-FR')}\n\n`;
    if (score != null) text += `${t('copy_score')} : ${score}/100\n`;
    if (meta.total_samples != null) {
      text += `${t('copy_samples')} : ${meta.total_samples}`;
      if (meta.positive_samples > 0) text += `, ${t('norma_dont')} ${meta.positive_samples} ${meta.positive_samples > 1 ? t('copy_positives') : t('copy_positive')}`;
      else if (meta.positive_samples === 0) text += `, ${t('copy_all_negative')}`;
      text += '\n';
    }
    text += `\n${t('copy_footer')}`;
    navigator.clipboard.writeText(text).then(() => alert(t('alert_copy_done')));
  };

  // `fetch` + en-tête, jamais `?token=` dans l'URL (cf `telechargerFichier`).
  const handleDownload = (type) => {
    const telecharger = type === 'html' ? telechargerRapport : telechargerExcel;
    telecharger(id).catch(e => alert(e.message));
  };

  const MessageItem = ({ msg }) => {
    const [expanded, setExpanded] = React.useState(false);
    const firstLine = msg.text ? msg.text.split('\n')[0] : '';
    return (
      <div
        onClick={() => !msg.isUser && setExpanded(e => !e)}
        style={{ padding: '7px 10px', borderRadius: 8, cursor: msg.isUser ? 'default' : 'pointer', transition: 'background 0.15s', borderLeft: msg.isUser ? '2px solid var(--blue)' : 'none', paddingLeft: msg.isUser ? 12 : 10 }}
        onMouseEnter={e => { if (!msg.isUser) e.currentTarget.style.background = 'rgba(128,128,128,0.08)'; }}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, fontSize: 14, lineHeight: 1.75, color: msg.isUser ? 'var(--blue)' : 'var(--text-muted)', overflow: expanded ? 'visible' : 'hidden', textOverflow: expanded ? 'unset' : 'ellipsis', whiteSpace: expanded ? 'normal' : 'nowrap' }}>
            {expanded ? renderText(msg.text) : firstLine}
          </div>
          {!msg.isUser && (
            <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0, marginTop: 4 }}>
              {expanded ? '▲' : '▼'}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
      ⏳ Chargement...
    </div>
  );

  if (error) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ fontSize: 14, color: '#dc2626' }}>❌ {error}</div>
      <button onClick={() => setLocation('/analyses')} style={{ fontSize: 13, color: 'var(--blue)', background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
      {t('back_to_analyses')}
      </button>
    </div>
  );

  let meta = {};
  try { meta = analysis?.metadata ? JSON.parse(analysis.metadata) : {}; } catch(_) {}
  const badge = BADGE[positionDuRapport(meta)] || null;
  const conformityScore = meta.conformity_score != null ? Math.round(meta.conformity_score) : null;
  const isCompleted = analysis?.status === 'COMPLETED';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
      <style>{`
        .av-msg-hover:hover { background: rgba(128,128,128,0.06) !important; }
      `}</style>

      {/* Zone messages scrollable */}
      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto', padding: '32px 24px 16px', minHeight: 0 }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>

          {/* ⚖️ Mention aide à la décision — NE PAS SUPPRIMER (obligation CGU Art. 5.3) */}
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderLeft: '4px solid #3b82f6',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
            <p style={{ fontSize: 13, color: '#1e40af', margin: 0, lineHeight: 1.5 }}>
              <strong>Outil d'aide à la décision.</strong> Ce résultat est fourni à titre indicatif
              par Normascan. Il ne constitue pas un avis professionnel certifié
              et ne saurait remplacer l'analyse d'un expert qualifié.
              La décision finale et toute responsabilité demeurent celles du professionnel utilisateur.
            </p>
          </div>

          {/* En-tête NORMA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'white', flexShrink: 0 }}>N</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--black)' }}>NORMA</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Assistant IA · NF X46-020</div>
            </div>
            {isCompleted && <span style={{ marginLeft: 8, fontSize: 16 }}>✅</span>}
          </div>

          {/* Messages NORMA de la session */}
          {messages.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 24 }}>
              {messages.map((msg, idx) => (
                <MessageItem key={idx} msg={msg} />
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: 24, fontStyle: 'italic' }}>
              {t('no_history')}
            </div>
          )}

          {/* Carte résultat */}
          {analysis && (
            <div style={{ borderRadius: 12, border: '1px solid var(--gray)', background: 'rgba(128,128,128,0.03)', padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {isCompleted ? '✅ ' : (analysis.status === 'CANCELLED' ? '🚫 ' : '⚙️ ')}{analysis.filename}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                  {new Date(analysis.upload_date).toLocaleDateString('fr-FR')}
                </span>
              </div>

              {isCompleted && (
                <>
                  {/*{badge && conformityScore != null && (
                    <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: badge.bg, border: `1px solid ${badge.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: badge.color }}>{t('conformity')}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: badge.color }}>{badge.label} · {conformityScore}/100</span>
                    </div>
                  )}*/}

                  {meta.total_samples != null && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                      🔬 {meta.total_samples} prélèvement{meta.total_samples > 1 ? 's' : ''}
                      {meta.positive_samples != null && (meta.positive_samples > 0
                        ? <span style={{ color: '#ef4444' }}> · ⚠️ {meta.positive_samples} positif{meta.positive_samples > 1 ? 's' : ''}</span>
                        : <span style={{ color: '#22c55e' }}> · ✅ Tous négatifs</span>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {analysis.report_html_path && (
                      <Button className="flex-1" onClick={() => handleDownload('html')}>{t('btn_dl_pdf')}</Button>
                    )}
                    {analysis.report_excel_path && (
                      <Button variant="outline" className="flex-1" onClick={() => handleDownload('excel')}>{t('btn_dl_excel')}</Button>
                    )}
                    <button
                      onClick={handleCopyResume}
                      style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--gray)', color: 'var(--black)', fontSize: 12, cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(128,128,128,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >{t('btn_copy_summary')}</button>
                  </div>
                </>
              )}

              {enCours && (
                <>
                  <ProgressBar progress={analysis.progress || 0} currentStep={analysis.current_step || ''} />
                  {peutAnnuler && (
                    <div style={{ marginTop: 12 }}>
                      <Button variant="destructive" className="w-full" onClick={handleCancel}>
                        {t('analyse_btn_annuler')}
                      </Button>
                    </div>
                  )}
                </>
              )}

              {analysis.status === 'CANCELLED' && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('analyse_annulee')}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Barre bas — version figée (pas d'upload, juste le label) */}
      <div style={{ borderTop: '1px solid var(--gray)', background: 'var(--bg-surface)', padding: '12px 24px 14px', flexShrink: 0 }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1.5px solid var(--gray)', borderRadius: 14, padding: '10px 14px', background: 'var(--white)' }}>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text-muted)' }}>{t('archived_label')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 5 }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)'}}>Norma V{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisView;